import { useEffect, useState } from "react";
import axios from "axios";
import { RiExportFill } from "react-icons/ri";
import { GlobalTable } from "../../../global/components/GlobalTable";
import { useNavigate, useSearchParams } from "react-router-dom";
import useGet from "../../../global/hooks/useGet";
import { PaymentColumns } from "./Columns";
import ConfirmModal from "../../../global/components/ConfirmModal";
import usePost from "../../../global/hooks/usePost";
import {
  openConfirmNotification,
  openErrorNotification,
} from "../../../global/utils/toast";
import { InvoiceFilter } from "../Components/FilterComponent";
import CommonBreadcrumb from "../../../global/components/CommonBreadcrumb";
import CommonButton from "../../../global/components/CommonButton";
import { downloadPaymentCSV } from "../utils/downloadPaymentCsv";
import { getDealerNo, getOemNo, hasRole, getUserData } from "../../../global/hooks/getUserRole";

const paymentTimeToUnix = (value) => {
  if (value && typeof value.unix === "function") {
    return value.unix();
  }
  const ms = new Date(value).getTime();
  return Number.isNaN(ms) ? value : Math.floor(ms / 1000);
};

const getPaymentExportQueryString = (searchParams, { isDealerAccount, isOemAccount }) => {
  const q = new URLSearchParams(searchParams);
  q.delete("pageSize");
  q.delete("total");
  q.delete("current");
  q.delete("limit");
  q.delete("page");
  if (isDealerAccount) {
    const dn = getDealerNo();
    if (dn != null && dn !== "") {
      q.set("DealerNo", String(dn));
    }
  }
  if (isOemAccount) {
    const on = getOemNo();
    if (on != null && on !== "") {
      q.set("OemNo", String(on));
    }
  }
  return q.toString();
};

const initialFilterState = {
  PaymentNo: "",
  PaymentTime: "",
  OemNo: "",
  DealerNo: "",
  PaymentMode: "",
  PaidTo: "",
};

export const Payment = () => {
  const [listData, setListData] = useState([]);
  const [params, setParams] = useSearchParams({
    limit: 10,
    sort_by: "PaymentNo",
    order: "desc",
  });
  const modParams = { ...Object.fromEntries([...params?.entries()]) } || {};
  const [pagination, setPagination] = useState({
    current: parseInt(modParams.page) || 1,
    pageSize: parseInt(modParams.limit) || 10,
    total: 0,
  });
  const [filter, setFilter] = useState(initialFilterState);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [filterNoValueText, setFilterNoValueText] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const logData = getUserData();
  const isDealerAccount = hasRole(7, 8);
  const isOemAccount = hasRole(4, 5, 6);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const emailUrl = `${process.env.REACT_APP_API_URL}/api/payments/send_invoice`;
  const {
    data: emailData,
    loading: emailLoading,
    error: emailError,
    postData: sentEmail,
  } = usePost(emailUrl);

  const navigate = useNavigate();

  const breadcrumbItems = [
    {
      label: "Payment",
      href: "/payment",
    },
  ];

  useEffect(() => {
    const matchedFilter = {};
    for (const key in initialFilterState) {
      if (modParams[key] !== undefined) {
        matchedFilter[key] = modParams[key];
      }
    }
    setFilter((prev) => ({
      ...prev,
      ...matchedFilter,
    }));
  }, []);

  // to get table data from API server (DealerNo for dealer login is added here only, not in the URL bar)
  const queryParams = new URLSearchParams(params);
  queryParams.delete("pageSize");
  queryParams.delete("total");
  queryParams.delete("current");
  if (isDealerAccount) {
    const dn = getDealerNo();
    if (dn != null && dn !== "") {
      queryParams.set("DealerNo", String(dn));
    }
  }
  if (isOemAccount) {
    const on = getOemNo();
    if (on != null && on !== "") {
      queryParams.set("OemNo", String(on));
    }
  }
  const query = queryParams.toString();
  const url = query
    ? `${process.env.REACT_APP_API_URL}/api/payments/?${query}`
    : `${process.env.REACT_APP_API_URL}/api/payments/`;
  const { data, loading, error, refetch } = useGet(url);

  const paymentExportQs = getPaymentExportQueryString(params, {
    isDealerAccount,
    isOemAccount,
  });
  const paymentExportUrl = paymentExportQs
    ? `${process.env.REACT_APP_API_URL}/api/payments/export/?${paymentExportQs}`
    : `${process.env.REACT_APP_API_URL}/api/payments/export/`;

  const paginationConfig = {
    current: pagination.current, //current page
    pageSize: pagination.pageSize, // items per page
    total: data?.count, // total items
    showSizeChanger: true,
    pageSizeOptions: [10, 20, 50, 100],
    showTotal: (total, range) => `Total ${total} items ${range[0]}-${range[1]}`,
    onChange: (page, pageSize) => {
      setPagination({
        ...pagination,
        current: page,
        pageSize: pageSize,
      });

      const updatedParams = {
        ...Object.fromEntries([...params.entries()]),
        limit: pageSize,
        page: page,
      };
      setParams(updatedParams);
    },
  };

  const onSelectChange = (newSelectedRowKeys, newSelectedRow) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
    fixed: true,
  };

  const onRow = (record) => {
    return {
      // onClick: (event) => {
      //   navigate(`/payment/info/${record?.InvoiceNo}`);
      // },
    };
  };

  const handleFilterChange = (e) => {
    const inputName = e.target.name;
    const inputValue = e.target.value;
    setFilter({ ...filter, [inputName]: inputValue });
  };

  const handleFilter = (e) => {
    const filterDict = {};
    for (const key in filter) {
      if (
        filter[key] !== null &&
        filter[key] !== "" &&
        filter[key] !== undefined
      ) {
        if (key === "PaymentTime") {
          filterDict[key] = paymentTimeToUnix(filter[key]);
        } else {
          filterDict[key] = filter[key];
        }
      }
    }
    const isEmpty = JSON.stringify(filterDict) === "{}";

    if (isEmpty) {
      setFilterNoValueText("No value exists!");
      handleReset();
    } else {
      setPagination((prev) => ({ ...prev, current: 1 }));
      setParams({
        limit: 10,
        sort_by: "PaymentNo",
        order: "desc",
        page: 1,
        ...filterDict,
      });
    }
  };

  //email functionality
  const handleEmail = () => {
    sentEmail({ ids: selectedRowKeys });
  };

  //sent invitation
  const handleInvitation = (e) => {
    setIsModalOpen(true);
  };
  // reset
  const handleReset = (e) => {
    setFilter(initialFilterState);
    setPagination({
      current: 1,
      pageSize: 10,
      total: 0,
    });
    const params = {
      limit: 10,
      sort_by: "PaymentNo",
      order: "desc",
    };
    setParams(params);
    refetch();
    setSelectedRowKeys([]);
  };

  useEffect(() => {
    if (data && !loading) {
      const newData = data?.data?.map((el) => ({
        ...el,
        key: el?.PaymentNo,
      }));
      setListData(newData);
    }
  }, [data, loading]);

  //toast for email
  useEffect(() => {
    if (emailError) {
      openErrorNotification(
        emailError?.response?.data?.message || "Something went wrong",
      );
    } else if (!emailLoading && !emailError && emailData) {
      openConfirmNotification("Success");

      // setTimeout(() => {
      //   navigate(-1);
      // }, 200);
    }
  }, [emailError, emailData, emailLoading]);

  const handlePaymentExport = async () => {
    const token = getUserData();
    const access_token = token ? token.access_token : null;
    try {
      setExportLoading(true);
      const response = await axios.get(paymentExportUrl, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${access_token}`,
        },
      });
      const payload = response.data;
      if (
        payload?.success === false ||
        payload?.message === "No payments found." ||
        payload?.message === "No data"
      ) {
        openErrorNotification(payload.message || "No Data Found");
        return;
      }
      const rows = payload?.data || payload;
      if (!Array.isArray(rows) || rows.length === 0) {
        openErrorNotification("No Data Found");
        return;
      }
      const timestamp = new Date().toISOString().split("T")[0];
      downloadPaymentCSV(payload, `Payment-Export_${timestamp}.csv`, {
        includePaidTo: [1, 2, 4, 5].includes(logData?.role_value),
      });
      openConfirmNotification("Success");
    } catch (err) {
      openErrorNotification(
        err?.response?.data?.message ||
          err?.message ||
          "Something went wrong.",
      );
    } finally {
      setExportLoading(false);
    }
  };

  const buttonData = [
    {
      label: "Export",
      icon: <RiExportFill className="!text-[14px] font-medium" />,
      onClick: handlePaymentExport,
      loading: exportLoading,
    },
  ];

  return (
    <div>
      <div className="pb-4 -mt-4 pt-[6px]">
        <CommonBreadcrumb breadcrumbItems={breadcrumbItems}>
          <CommonButton buttonsData={buttonData} />
        </CommonBreadcrumb>
      </div>
      <div className="p-4 bg-white rounded-[4px] border border-gray-200">
        <InvoiceFilter
          filter={filter}
          handleFilterChange={handleFilterChange}
          handleFilter={handleFilter}
          handleReset={handleReset}
          setShowEmailModal={setShowEmailModal}
          setShowModal={setShowModal}
          selectedRowKeys={selectedRowKeys}
          setFilter={setFilter}
          handleInvitation={handleInvitation}
          params={params}
        />
        <ConfirmModal
          showModal={showModal}
          setShowModal={setShowModal}
          setSelectedRowKeys={setSelectedRowKeys}
        />
        <GlobalTable
          columns={PaymentColumns(
            setParams,
            params,
            logData?.role_value === 1 || logData?.role_value === 3,
            isDealerAccount,
          )}
          dataSource={listData}
          // rowSelection={rowSelection}
          pagination={paginationConfig}
          // onRow={(record, rowIndex) => {
          //   return {
          //     onMouseEnter: () => setHoveredRow(rowIndex),
          //     onMouseLeave: () => setHoveredRow(null),
          //     ...onRow?.(record, rowIndex),
          //   };
          // }}
          loading={loading}
        />
        <ConfirmModal
          showModal={showEmailModal}
          setShowModal={setShowEmailModal}
          onDeleteConfirm={handleEmail}
          content="Do you want to send Email?"
        />
      </div>
    </div>
  );
};
